<script setup lang="ts">
import SchoolClassInfoTab from 'src/modules/School/components/SchoolClassInfoTab/SchoolClassInfoTab.vue';
import SchoolClassStudentsTab from 'src/modules/School/components/SchoolClassStudentsTab/SchoolClassStudentsTab.vue';
import SchoolClassInviteLinks from 'src/modules/School/components/SchoolClassInviteLinks/SchoolClassInviteLinks.vue';
import SchoolClassCoursesTab from 'src/modules/School/components/SchoolClassCoursesTab/SchoolClassCoursesTab.vue';
import SchoolClassSectionsTab from 'src/modules/School/components/SchoolClassSectionsTab/SchoolClassSectionsTab.vue';
import SchoolAssignmentMatrixDialog from 'src/modules/School/components/SchoolAssignmentMatrixDialog/SchoolAssignmentMatrixDialog.vue';
import SchoolStudentStatsDialog from 'src/modules/School/components/SchoolStudentStatsDialog/SchoolStudentStatsDialog.vue';
import { useSchoolClassDetailView } from 'src/modules/School/composables/useSchoolClassDetailView';
import { useTeacherPreview } from 'src/modules/School/composables/useTeacherPreview';
import { computed } from 'vue';
import {
  mdiAccountMultipleOutline,
  mdiAccountCog,
  mdiAccountGroup,
  mdiBookOpenPageVariantOutline,
  mdiBookEducation,
  mdiCalendarRangeOutline,
  mdiInformationOutline,
  mdiSchool,
  mdiViewList,
} from '@quasar/extras/mdi-v7';

const {
  activeTab,
  visibleTabs,
  classDetail,
  assignedCourses,
  totalAssignedCourses,
  assignedCoursesSearch,
  isAssignedCoursesExpanded,
  selectedCourseDocumentId,
  selectedAssignedCourse,
  progressRows,
  assignedSections,
  totalAssignedSections,
  assignedSectionsSearch,
  isAssignedSectionsExpanded,
  selectedSectionDocumentId,
  selectedAssignedSection,
  sectionProgressRows,
  isClassDataLoading,
  showClassLoadError,
  hasSelectedSemester,
  selectedSemester,
  semesterState,
  allowFutureAssignments,
  canAddAssignments,
  canInviteForSelectedSemester,
  isProgressLoading,
  isSectionProgressLoading,
  canManageClassCourses,
  canDecide,
  handleDecision,
  isAssignmentDialogOpen,
  assignmentDialogTab,
  assignmentDialogStudentIds,
  assignmentDialogCourseIds,
  assignmentDialogSectionIds,
  studentOptionItems,
  courseOptionItems,
  sectionOptionItems,
  isApplyingAssignments,
  onSelectAssignedCourse,
  onSelectAssignedSection,
  openAssignmentDialogFromOverview,
  applyAssignmentSelections,
  updateCourseStudentAssignment,
  updateSectionStudentAssignment,
  isStudentStatsDialogOpen,
  statsStudentName,
  studentStatsSummary,
  studentStatsLoading,
  studentStatsError,
  openStudentStats,
} = useSchoolClassDetailView();

// Preview walkthroughs are for teachers — the ones deciding whether the
// content fits their class. `canDecide` is exactly "current user is a teacher".
const { previewLoadingDocumentId, previewCourse, previewSection } =
  useTeacherPreview();

function handlePreviewCourse(courseDocumentId: string) {
  const course = assignedCourses.value.find(
    (c) => c.documentId === courseDocumentId,
  );
  void previewCourse(courseDocumentId, course?.id);
}

function handlePreviewSection(sectionDocumentId: string) {
  void previewSection(sectionDocumentId);
}

const studentsCount = computed(
  () => classDetail.value?.enrollments?.length || 0,
);
const teachersCount = computed(() => classDetail.value?.teachers?.length || 0);

/*
 * Green = running now, orange = not started yet, grey = over. Grey rather than red
 * for a past semester: it is finished, not broken.
 */
const semesterTagColor = computed(() => {
  if (semesterState.value === 'current') return 'positive';
  if (semesterState.value === 'future') return 'warning';
  return 'grey-6';
});

/*
 * The outline tag has no fill, so its `color` IS the ink (Quasar derives outline
 * chip ink from `color`, ignoring `text-color`). $positive/$warning as text only
 * reach 3.2:1 / 2.3:1 on a light surface, so use the theme-aware ink tokens.
 */
const semesterTagInk = computed(() => {
  if (semesterState.value === 'current') return 'ink-positive';
  if (semesterState.value === 'future') return 'ink-warning';
  return 'ink-neutral';
});

const semesterTagKey = computed(() => {
  if (semesterState.value === 'current') return 'SEMESTER_TAG_CURRENT';
  if (semesterState.value === 'future') return 'SEMESTER_TAG_UPCOMING';
  return 'SEMESTER_TAG_PAST';
});
</script>

<template>
  <div class="school-class-detail-page">
    <div class="q-pa-md column q-gutter-y-md school-class-detail-container">
      <q-inner-loading :showing="isClassDataLoading">
        <q-spinner-gears
          size="60px"
          color="primary"
        />
      </q-inner-loading>

      <q-banner
        v-if="!hasSelectedSemester"
        :class="$q.dark.isActive ? 'bg-grey-9 text-white' : 'bg-grey-3 text-dark'"
      >
        {{ $t('SCHOOL_SELECT_SEMESTER_TO_VIEW') }}
      </q-banner>

      <q-banner
        v-else-if="showClassLoadError"
        :class="$q.dark.isActive ? 'bg-grey-9 text-white' : 'bg-grey-3 text-dark'"
      >
        {{ $t('ACTION_FAILED') }}
      </q-banner>

      <template v-if="classDetail">
        <q-card
          bordered
          class="class-overview-card"
        >
          <q-card-section class="row q-col-gutter-md items-stretch">
            <div class="col-12 col-md">
              <div class="text-overline text-primary">
                {{ $t('INFORMATION') }}
              </div>
              <div class="text-h5 text-weight-bold">{{ classDetail.name }}</div>
              <div class="text-subtitle2 text-muted">
                {{ classDetail.code }}
              </div>
              <div class="text-caption text-muted q-mt-sm">
                {{
                  classDetail.school?.short_name ||
                  classDetail.school?.full_name ||
                  ''
                }}
              </div>

              <!--
                Which semester this page is showing. The same class looks identical
                in a finished term, the running one and one that hasn't started, so
                the name is spelled out and the state carries the colour.
              -->
              <div
                v-if="selectedSemester"
                class="row items-center q-gutter-xs q-mt-sm"
              >
                <q-chip
                  dense
                  :color="semesterTagColor"
                  text-color="dark"
                  :icon="mdiCalendarRangeOutline"
                >
                  {{ $t('SCHOOL_SEMESTER_LABEL', { name: selectedSemester.name }) }}
                </q-chip>
                <q-chip
                  dense
                  outline
                  :color="semesterTagInk"
                >
                  {{ $t(semesterTagKey) }}
                </q-chip>

                <!--
                  Future semesters only: assigning ahead of a term is the exception,
                  so it is off until the teacher asks for it (and resets on reload).
                -->
                <q-toggle
                  v-if="semesterState === 'future' && canManageClassCourses"
                  v-model="allowFutureAssignments"
                  dense
                  color="warning"
                  size="sm"
                  :label="$t('SCHOOL_ALLOW_FUTURE_ASSIGNMENTS')"
                  class="q-ml-sm text-caption"
                />
              </div>
            </div>

            <div class="col-12 col-md-auto class-overview-actions-col">
              <div class="column class-overview-actions-inner">
                <div class="row q-gutter-sm justify-end">
                  <q-chip
                    color="secondary"
                    text-color="dark"
                    :icon="mdiAccountGroup"
                  >
                    {{ $t('STUDENTS') }}: {{ studentsCount }}
                  </q-chip>
                  <q-chip
                    color="grey-3"
                    text-color="dark"
                    :icon="mdiSchool"
                  >
                    {{ $t('TEACHERS') }}: {{ teachersCount }}
                  </q-chip>
                  <q-chip
                    color="blue-1"
                    text-color="dark"
                    :icon="mdiBookEducation"
                  >
                    {{ $t('COURSES') }}: {{ totalAssignedCourses }}
                  </q-chip>
                  <q-chip
                    color="teal-1"
                    text-color="dark"
                    :icon="mdiViewList"
                  >
                    {{ $t('SECTIONS') }}: {{ totalAssignedSections }}
                  </q-chip>
                </div>
                <!--
                  The dialog only ever adds assignments, so a locked semester
                  disables it outright rather than opening a dialog that refuses.
                -->
                <q-btn
                  v-if="canManageClassCourses"
                  class="q-mt-sm"
                  color="primary"
                  unelevated
                  :disable="!canAddAssignments"
                  :icon="mdiAccountCog"
                  :label="$t('ASSIGNMENTS')"
                  @click="openAssignmentDialogFromOverview"
                >
                  <q-tooltip v-if="!canAddAssignments">
                    {{
                      semesterState === 'past'
                        ? $t('SCHOOL_ASSIGN_LOCKED_PAST')
                        : $t('SCHOOL_ASSIGN_LOCKED_FUTURE')
                    }}
                  </q-tooltip>
                </q-btn>
              </div>
            </div>
          </q-card-section>
        </q-card>

        <q-card
          bordered
          class="class-tabs-card"
        >
          <q-tabs
            v-model="activeTab"
            class="class-tabs"
            align="left"
            active-color="primary"
            indicator-color="primary"
            mobile-arrows
          >
            <q-tab
              v-if="visibleTabs.includes('info')"
              name="info"
              :icon="mdiInformationOutline"
              :label="$t('INFORMATION')"
            />
            <q-tab
              v-if="visibleTabs.includes('students')"
              name="students"
              :icon="mdiAccountMultipleOutline"
              :label="$t('STUDENTS')"
            />
            <q-tab
              v-if="visibleTabs.includes('courses')"
              name="courses"
              :icon="mdiBookOpenPageVariantOutline"
              :label="$t('COURSES')"
            />
            <q-tab
              v-if="visibleTabs.includes('sections')"
              name="sections"
              :icon="mdiViewList"
              :label="$t('SECTIONS')"
            />
          </q-tabs>
          <q-separator />
          <q-tab-panels
            v-model="activeTab"
            animated
            class="bg-transparent class-tabs-panels"
          >
            <q-tab-panel name="info">
              <SchoolClassInfoTab :class-detail="classDetail" />
            </q-tab-panel>

            <q-tab-panel name="students">
              <SchoolClassInviteLinks
                v-if="canDecide && canInviteForSelectedSemester"
                class="q-mb-md"
                :class-document-id="classDetail.documentId"
                :semester="selectedSemester"
              />
              <SchoolClassStudentsTab
                :class-detail="classDetail"
                :can-decide="canDecide"
                @decide="handleDecision"
                @view-stats="openStudentStats"
              />
            </q-tab-panel>

            <q-tab-panel
              v-if="canManageClassCourses"
              name="courses"
            >
              <SchoolClassCoursesTab
                :assigned-courses="assignedCourses"
                :total-assigned-courses="totalAssignedCourses"
                :search="assignedCoursesSearch"
                :is-expanded="isAssignedCoursesExpanded"
                :selected-course-document-id="selectedCourseDocumentId"
                :selected-course-name="selectedAssignedCourse?.course_name"
                :progress-rows="progressRows"
                :is-progress-loading="isProgressLoading"
                :can-add="canAddAssignments"
                :show-preview="canDecide"
                :preview-loading-document-id="previewLoadingDocumentId"
                @update:search="assignedCoursesSearch = $event"
                @toggle-expanded="
                  isAssignedCoursesExpanded = !isAssignedCoursesExpanded
                "
                @select-course="onSelectAssignedCourse"
                @preview-course="handlePreviewCourse"
                @set-student-assignment="updateCourseStudentAssignment"
              />
            </q-tab-panel>

            <q-tab-panel
              v-if="canManageClassCourses"
              name="sections"
            >
              <SchoolClassSectionsTab
                :assigned-sections="assignedSections"
                :total-assigned-sections="totalAssignedSections"
                :search="assignedSectionsSearch"
                :is-expanded="isAssignedSectionsExpanded"
                :selected-section-document-id="selectedSectionDocumentId"
                :selected-section-name="selectedAssignedSection?.section_name"
                :progress-rows="sectionProgressRows"
                :is-progress-loading="isSectionProgressLoading"
                :can-add="canAddAssignments"
                :show-preview="canDecide"
                :preview-loading-document-id="previewLoadingDocumentId"
                @update:search="assignedSectionsSearch = $event"
                @toggle-expanded="
                  isAssignedSectionsExpanded = !isAssignedSectionsExpanded
                "
                @select-section="onSelectAssignedSection"
                @preview-section="handlePreviewSection"
                @set-student-assignment="updateSectionStudentAssignment"
              />
            </q-tab-panel>
          </q-tab-panels>
        </q-card>

        <SchoolAssignmentMatrixDialog
          v-model="isAssignmentDialogOpen"
          :student-options="studentOptionItems"
          :course-options="courseOptionItems"
          :section-options="sectionOptionItems"
          :selected-student-ids="assignmentDialogStudentIds"
          :selected-course-ids="assignmentDialogCourseIds"
          :selected-section-ids="assignmentDialogSectionIds"
          :initial-tab="assignmentDialogTab"
          :is-applying="isApplyingAssignments"
          @apply="applyAssignmentSelections"
        />

        <SchoolStudentStatsDialog
          v-model="isStudentStatsDialogOpen"
          :student-name="statsStudentName"
          :summary="studentStatsSummary"
          :is-loading="studentStatsLoading"
          :error="studentStatsError"
        />
      </template>
    </div>
  </div>
</template>

<style scoped>
.school-class-detail-container {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}

.class-overview-card {
  border-radius: 14px;
  background: linear-gradient(
    180deg,
    var(--surface-card) 0%,
    var(--surface-elevated) 100%
  );
}

.class-tabs-card {
  border-radius: 14px;
  overflow: hidden;
}

.class-tabs {
  padding: 4px 8px;
  background: var(--surface-elevated);
}

.class-overview-actions-col {
  display: flex;
}

.class-overview-actions-inner {
  width: 100%;
  min-height: 100%;
  justify-content: space-between;
  align-items: flex-end;
}

@media (max-width: 599px) {
  .school-class-detail-page {
    overflow-x: hidden;
  }

  .school-class-detail-container {
    padding: 8px !important;
  }

  .class-tabs :deep(.q-tab) {
    min-width: 52px;
    padding: 0 6px;
  }

  .class-tabs :deep(.q-tab__label) {
    font-size: 10px;
    line-height: 1.1;
  }

  .class-tabs :deep(.q-tab__icon) {
    font-size: 18px;
  }

  .class-overview-actions-inner {
    align-items: stretch;
  }

  .class-overview-actions-inner .row {
    justify-content: flex-start;
  }

  .class-overview-actions-inner .q-btn {
    width: 100%;
  }

  .class-tabs-panels :deep(.q-tab-panel) {
    padding: 12px;
  }
}
</style>
